import type { FilterConditionDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { formatDate, formatTime } from "@/shared/lib/date-time";
import { fetcher } from "@/shared/lib/fetcher";
import {
  hydrateLookupItem
} from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormInput,
  FormRoot,
  RHF,
  ScrollArea,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  zodResolver,
} from "@devhop/ui";
import { Loader2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import type { BulkUpdateClassSessionTemplatePreviewResultDTO } from "../../domain/contracts/sessions";
import {
  useBulkUpdateClassSessionTemplate,
  usePreviewBulkUpdateClassSessionTemplate,
} from "../api/queries";
import { useClassSessionFilterDisplay } from "../lib/class-session-filter-display";

type ClassTemplateItem = { id: string; name: string };

const FormSchema = z.object({
  templateId: z.string().min(1, "ຕ້ອງເລືອກແມ່ແບບຄລາດສ໌"),
  changeReason: z.string().min(1, "ຕ້ອງກຳນົດເຫດຜົນ"),
});

type BulkUpdateClassSessionTemplateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: FilterConditionDTO[];
};

export function BulkUpdateClassSessionTemplateDialog({
  open,
  onOpenChange,
  filters,
}: BulkUpdateClassSessionTemplateDialogProps) {
  const previewMutation = usePreviewBulkUpdateClassSessionTemplate();
  const bulkMutation = useBulkUpdateClassSessionTemplate();
  const [preview, setPreview] =
    useState<BulkUpdateClassSessionTemplatePreviewResultDTO | null>(null);
  const { items: filterItems, isLoading: filterLabelsLoading } =
    useClassSessionFilterDisplay(filters);

  const methods = RHF.useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      templateId: "",
      changeReason: "",
    },
  });

  const templateId = methods.watch("templateId");

  useEffect(() => {
    if (!open) {
      methods.reset();
      setPreview(null);
    }
  }, [open, methods.reset]);

  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    if (!open || !templateId) {
      setPreview(null);
      return;
    }
    const timer = setTimeout(() => {
      previewMutation.mutate(
        { filters, templateId },
        {
          onSuccess: (data) => setPreview(data),
          onError: () => setPreview(null),
        },
      );
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filtersKey stabilizes filters
  }, [open, templateId, filtersKey]);

  const onSubmit = methods.handleSubmit(async (vals) => {
    await bulkMutation.mutateAsync({
      filters,
      templateId: vals.templateId,
      changeReason: vals.changeReason.trim(),
    });
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ອັບເດດແມ່ແບບຄລາດສ໌ (Bulk)</DialogTitle>
          <DialogDescription>
            ປ່ຽນແມ່ແບບສຳລັບຊົ່ວໂມງຮຽນທີ່ຈອງແລ້ວຕາມຕົວກອງທີ່ເລືອກ
          </DialogDescription>
        </DialogHeader>

        <FormRoot methods={methods} onSubmit={onSubmit} className="space-y-4">
          <div className="rounded-lg border bg-muted/30 p-3">
            <p className="mb-2 font-medium text-sm">ຕົວກອງປັດຈຸບັນ</p>
            {filterItems.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                ບໍ່ມີຕົວກອງ — ກະລຸນາກັ່ນຕອງກ່ອນ
              </p>
            ) : filterLabelsLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Loader2Icon className="size-4 animate-spin" />
                ກຳລັງໂຫຼດ...
              </div>
            ) : (
              <dl className="grid gap-2 sm:grid-cols-2">
                {filterItems.map((item) => (
                  <div key={item.label} className="text-sm">
                    <dt className="text-muted-foreground">{item.label}</dt>
                    <dd className="font-medium">{item.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>

          <div className="space-y-2">
            <span className="font-medium text-sm">ແມ່ແບບໃໝ່</span>
            <RHF.Controller
              name="templateId"
              control={methods.control}
              render={({ field, fieldState }) => (
                <>
                  <InfiniteCombobox<ClassTemplateItem>
                    queryKey={["classes", "templates", "lookup", "bulk-update"]}
                    queryFn={async ({ search, pageParam }) => {
                      const limit = 20;
                      const skip = (pageParam - 1) * limit;
                      const url = new URL(
                        `${config.apiUrl}/classes/templates/lookup`,
                      );
                      url.searchParams.set("limit", String(limit));
                      url.searchParams.set("skip", String(skip));
                      if (search) url.searchParams.set("q", search);
                      const data = await fetcher.get<{
                        items: Array<{ id: string; name: string }>;
                        total: number;
                      }>(url.toString());
                      return {
                        items: data.items.map((t) => ({
                          id: t.id,
                          name: t.name,
                        })),
                        nextPage:
                          skip + limit < data.total ? pageParam + 1 : null,
                      };
                    }}
                    preloadQueryFn={(id) =>
                      hydrateLookupItem<ClassTemplateItem>(
                        `${config.apiUrl}/classes/templates/lookup`,
                        id,
                      ).then((item) => item ?? null)
                    }
                    getLabel={(item) => item.name}
                    getValue={(item) => item.id}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="ເລືອກແມ່ແບບຄລາດສ໌..."
                    className="w-full"
                    clearable
                  />
                  {fieldState.error?.message && (
                    <p className="text-destructive text-xs">
                      {fieldState.error.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>

          <FormInput
            name="changeReason"
            label="ເຫດຜົນການປ່ຽນແປງ"
            placeholder="ຕົວຢ່າງ: ປ່ຽນແມ່ແບບຕາມຄລາດສ໌ໃໝ່"
            requiredMark
          />

          {templateId && (
            <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
              <p className="font-medium text-sm">ສະຫຼຸບການອັບເດດ</p>
              {previewMutation.isPending ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2Icon className="size-4 animate-spin" />
                  ກຳລັງຄິດຄຳນວນ...
                </div>
              ) : preview ? (
                <>
                  <ul className="space-y-1 text-muted-foreground text-sm">
                    <li>
                      ຕົງກັບຕົວກອງ:{" "}
                      <span className="font-medium text-foreground">
                        {preview.totalMatching.toLocaleString("lo-LA")} ຊົ່ວໂມງຮຽນ
                      </span>
                    </li>
                    <li>
                      ຈອງແລ້ວ:{" "}
                      <span className="font-medium text-foreground">
                        {preview.scheduledMatching.toLocaleString("lo-LA")}{" "}
                        ຊົ່ວໂມງຮຽນ
                      </span>
                    </li>
                    <li>
                      ຈະອັບເດດ:{" "}
                      <span className="font-medium text-foreground">
                        {preview.updatableCount.toLocaleString("lo-LA")}{" "}
                        ຊົ່ວໂມງຮຽນ
                      </span>
                    </li>
                  </ul>

                  {preview.updatableCount === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      ບໍ່ມີຊົ່ວໂມງຮຽນທີ່ຕ້ອງອັບເດດ (ແມ່ແບບເດີມຫຼືບໍ່ມີສະຖານະຈອງແລ້ວ)
                    </p>
                  ) : preview.sessions.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-muted-foreground text-sm">
                        ລາຍການທີ່ຈະອັບເດດ
                        {preview.sessionsTruncated
                          ? ` (ສະແດງ ${preview.sessions.length.toLocaleString("lo-LA")} ຈາກ ${preview.updatableCount.toLocaleString("lo-LA")})`
                          : ""}
                      </p>
                      <ScrollArea className="h-[240px] rounded-md border bg-background">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>ວັນທີ</TableHead>
                              <TableHead>ເວລາ</TableHead>
                              <TableHead>ແມ່ແບບປັດຈຸບັນ</TableHead>
                              <TableHead>ຫ້ອງ</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {preview.sessions.map((session) => (
                              <TableRow key={session.id}>
                                <TableCell className="whitespace-nowrap">
                                  {formatDate(session.date)}
                                </TableCell>
                                <TableCell className="whitespace-nowrap">
                                  {formatTime(session.startTime)} –{" "}
                                  {formatTime(session.endTime)}
                                </TableCell>
                                <TableCell>{session.templateName}</TableCell>
                                <TableCell>
                                  {session.roomName ?? "—"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="text-muted-foreground text-sm">
                  ບໍ່ສາມາດຄິດຄຳນວນໄດ້
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="submit"
              disabled={
                bulkMutation.isPending ||
                !templateId ||
                !preview ||
                preview.updatableCount === 0
              }
            >
              {bulkMutation.isPending ? "ກຳລັງອັບເດດ..." : "ອັບເດດແມ່ແບບ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
